import React, { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { useAppStore } from '../../store/useAppStore'
import { Button } from '../ui/Button'
import { Icon } from '../ui/Icon'
import { isTauri, openAndReadFile } from '../../services/tauriService'

/**
 * 从 Markdown 内容中提取标题
 * 提取第一个 # 标题，如果没有则返回空字符串
 */
const extractTitleFromMarkdown = (content: string): string => {
  if (!content) return ''

  // 匹配第一个 # 标题（# 后面跟空格和标题内容）
  const titleMatch = content.match(/^#\s+(.+)$/m)
  if (titleMatch && titleMatch[1]) {
    return titleMatch[1].trim()
  }

  return ''
}

/**
 * 导入按钮组件
 * 点击打开文件选择器，导入本地 markdown 文件
 * 在 Tauri 桌面环境下使用原生文件对话框
 */
export const ImportButton: React.FC = () => {
  const { t } = useTranslation()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { saveDocument } = useAppStore()

  // 处理导入的内容（通用逻辑）
  const processImportedContent = (content: string, filePath?: string) => {
    // 从 markdown 内容中提取标题，或使用文件名
    const title = extractTitleFromMarkdown(content) || 
      (filePath ? filePath.split('/').pop()?.replace(/\.(md|markdown)$/, '') : '')

    // 通过 saveDocument 创建新文档（当 currentDocument 为 null 时会创建新文档）
    const { setCurrentDocument, setCurrentFilePath } = useAppStore.getState()
    setCurrentDocument(null)
    
    // 如果在 Tauri 环境，保存文件路径以便后续 Ctrl+S 保存
    if (filePath) {
      setCurrentFilePath(filePath)
    }
    
    saveDocument(content, title || undefined)
    toast.success(t('import.success'))
  }

  // Tauri 环境：使用原生文件对话框
  const handleTauriImport = async () => {
    try {
      const result = await openAndReadFile()
      if (result) {
        processImportedContent(result.content, result.path)
      }
    } catch (error) {
      console.error('Failed to import file (Tauri):', error)
      toast.error(t('import.failed'))
    }
  }

  // 处理文件选择（Web 环境）
  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    // 检查文件类型
    if (!file.name.endsWith('.md') && !file.name.endsWith('.markdown')) {
      toast.error(t('import.selectFile'))
      return
    }

    try {
      // 读取文件内容
      const content = await file.text()
      processImportedContent(content)

      // 重置文件输入
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    } catch (error) {
      console.error('Failed to import file:', error)
      toast.error(t('import.failed'))
    }
  }

  // 触发文件选择
  const handleClick = () => {
    // 在 Tauri 桌面环境使用原生对话框
    if (isTauri()) {
      handleTauriImport()
    } else {
      // Web 环境使用传统的 file input
      fileInputRef.current?.click()
    }
  }

  return (
    <>
      {/* Web 环境的文件输入（Tauri 环境不需要） */}
      {!isTauri() && (
        <input
          ref={fileInputRef}
          type="file"
          accept=".md,.markdown,text/markdown"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />
      )}
      <Button
        onClick={handleClick}
        variant="secondary"
        size="sm"
        className="gap-2"
      >
        <Icon name="import" size={16} />
        {t('nav.import')}
      </Button>
    </>
  )
}
